import { Box, CircularProgress, Dialog } from '@material-ui/core';
import { SBase, SButton } from '@setvi/shared/components';
import {
  AnalyticsModules,
  DBEventType,
  useAnalytics,
  useDatabricksAnalytics,
  useSubscribedMutation
} from '@setvi/shared/hooks';
import { ProductCard } from 'pages/auto-rfq/components';
import { RfqMatchOverride, RFQProducts } from 'pages/auto-rfq/interfaces';
import { ProductActionType } from 'pages/products/utils';
import { useDialog } from '@setvi/shared/providers';
import { useMemo, useState } from 'react';
import {
  CompanyProductUserType,
  Product,
  ProductDetailsTabsIds
} from '@setvi/shared/interfaces';
import {
  formatProduct,
  generateManualEntry,
  getItemAnalyticsBucket,
  getManualEntry,
  getProductChange
} from 'pages/auto-rfq/utils';
import { useRFQStore } from 'pages/auto-rfq/store';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { useQuoteStore } from 'pages/quotes/store';
import { ProductDetails } from 'componentsV2';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import {
  AutoRFQQueryKey,
  getRfqItemQuery,
  QuotesQueryKey,
  updateQuoteItemMutation
} from 'Services';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getProductsPrices } from 'pages/products/api';
import { PricingMode, useRfqConfigStore } from 'pages/auto-rfq/store/rfqConfig';
import BaseDialog, { Tabs } from '../base-dialog';
import ProductMatch from '../product-match';
import Catalog from '../catalog';
import ManualEntry, { ManualProps } from '../manual-entry';
import { useUpdateItem } from '../products/hook/useUpdateItem';
import { useStyles } from '../products/styled';
import { updateQuoteItems } from '../../quote-tab/utils';

interface ProductMatchDetailsProps {
  onClose: () => void;
  rfqItemId?: string;
  productId?: string;
  product: any;
}

const ProductMatchDetails = ({
  onClose,
  rfqItemId,
  productId,
  product: importedProduct
}: ProductMatchDetailsProps) => {
  const { id: rfqId } = useParams();
  const { dialogPaper } = useStyles();
  const { companyData } = useAppContext();

  const { captureDBAnalytics } = useDatabricksAnalytics();
  const { openDialog, closeDialog } = useDialog();
  const { captureAnalytics } = useAnalytics();

  const { quote, setQuote } = useQuoteStore();
  const { pricing } = useRfqConfigStore();

  const { products, updateProcessedProduct } = useRFQStore();
  const { handleItemUpdate, areItemsUpdating } = useUpdateItem({
    rfqId,
    triggerApis: []
  });
  const { mutateAsync: updatePrices, isLoading: arePricesLoading } =
    useMutation(getProductsPrices());
  const { mutateAsync: updateQuoteItem, isLoading } = useSubscribedMutation(
    updateQuoteItemMutation(),
    [QuotesQueryKey.PREVIEW_PDF, AutoRFQQueryKey.GET_ITEMS]
  );

  const [productDetails, setProductDetails] = useState<Product | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [modId, setModId] = useState<string>();
  const [newAnalytics, setNewAnalytics] = useState<any>();
  const [product, setProduct] = useState(importedProduct);
  const [pendingQuoteProduct, setPendingQuoteProduct] = useState<any>(null);

  const { isFetching: isRfqItemFetching } = useQuery({
    ...getRfqItemQuery(rfqItemId || ''),
    enabled: !!rfqItemId,
    onSuccess: async data => {
      const formated = await formatProduct(data);
      setProduct(formated);
    }
  });

  const userType = useMemo(
    () => companyData.Settings[38]?.Value as CompanyProductUserType,
    [companyData.Settings]
  );

  const productGetter = (p: RFQProducts) =>
    p?.matchedInternalProducts?.[p?.internalIndex]
      ? {
          ...p?.matchedInternalProducts?.[p?.internalIndex],
          competitorId: p?.id
        }
      : null;

  const internalProducts = product?.matchedInternalProductsId?.filter(
    ({ reasoning, percentage }: { reasoning: string; percentage: number }) =>
      reasoning || percentage
  );

  const matchedProduct =
    product?.matchedInternalProducts?.[product?.internalIndex];

  const fetchProductPricing = async (
    currProduct: any,
    existingItem: any,
    productData: any
  ) => {
    let newQuoteProduct = {
      ...existingItem,
      ...currProduct,
      sell: currProduct.cost ? Number(currProduct.cost) : 0,
      cost: currProduct.cost ? Number(currProduct.cost) : 0,
      sku: currProduct?.sku,
      name: currProduct?.name || '',
      rfqItemId: productData?.id,
      isFreeHand: false
    };

    if (productData?.manualEntry) {
      newQuoteProduct = {
        quantity: currProduct?.quantity || 1,
        vendor: currProduct?.vendor || {},
        sell: productData.cost ? Number(productData.cost) : 0,
        cost: productData.cost ? Number(productData.cost) : 0,
        sku: currProduct?.sku,
        name: currProduct?.name || '',
        note: existingItem?.note || '',
        rfqItemId: productData?.id,
        isFreeHand: true,
        images: [],
        manufacturerSku: '',
        type: 'product'
      };
    }

    if (pricing.mode !== PricingMode.NO_PRICING && !productData?.manualEntry) {
      try {
        const response = await updatePrices({
          body: {
            accountNumber:
              quote?.account?.accountNumber || quote?.segment?._id || '',
            products: [{ sku: currProduct.sku, quantity: 1 }]
          }
        });
        const { sell, cost } = response?.pricing?.[0];

        const totalGrossProfit = sell - cost || 0;
        const calculatedPercentage = (totalGrossProfit / sell) * 100;
        const totalGrossProfitPercentage = Number.isFinite(calculatedPercentage)
          ? calculatedPercentage
          : 0;

        newQuoteProduct = {
          ...newQuoteProduct,
          sell,
          ...(Number(cost) ? { cost } : {}),
          totalGrossProfit,
          totalGrossProfitPercentage
        };

        // Update product with sell value
        setProduct((prev: any) => ({ ...prev, sell }));
      } catch (error) {
        enqueueSnackbar('Error occured while fetching product price', {
          variant: 'error',
          autoHideDuration: 8000
        });
      }
    }

    return newQuoteProduct;
  };

  const handleManualEntry = async (props: ManualProps, i: number) => {
    const { id, modifiedElement } = getManualEntry(products, props, i);

    setProduct(modifiedElement);
    setModId(id);

    // Fetch pricing if quote exists and product is in quote
    if (quote?._id && modifiedElement?.id && modifiedElement?.inQuote) {
      const quoteItems = quote.items || [];
      const findItem = quoteItems?.find(
        ({ currRfqItemId }: any) => currRfqItemId === modifiedElement?.id
      );
      const itemNote =
        quoteItems?.find(
          ({ rfqItemId: autoRfqId }: any) => autoRfqId === modifiedElement?.id
        )?.note || '';

      const existingItem = {
        ...findItem,
        note: itemNote
      };

      const currProduct =
        modifiedElement?.matchedInternalProducts?.[
          modifiedElement?.internalIndex
        ];

      if (currProduct) {
        const quoteProduct = await fetchProductPricing(
          currProduct,
          existingItem,
          modifiedElement
        );
        setPendingQuoteProduct(quoteProduct);
      }
    }

    closeDialog('more-matches');
  };

  const handleManualApiEntry = async (props: ManualProps) => {
    const newProps = {
      ...props,
      price: Number(props.price),
      cost: Number(props.price)
    };

    const newProduct = {
      ...generateManualEntry(newProps),
      external: true
    };

    const modifiedElement = {
      ...product,
      manualEntry: true,
      percentage: 0,
      matchOverride: RfqMatchOverride.UserSelection,
      prevItemPercentage:
        product?.matchedInternalProducts?.[product?.internalIndex]
          ?.percentage || product?.prevItemPercentage,
      internalIndex: product?.matchedInternalProducts?.length,
      matchedInternalProducts: [...product?.matchedInternalProducts, newProduct]
    };

    setProduct(modifiedElement);
    setModId(product.id);

    // Fetch pricing if quote exists and product is in quote
    if (quote?._id && modifiedElement?.id && modifiedElement?.inQuote) {
      const quoteItems = quote.items || [];
      const findItem = quoteItems?.find(
        ({ currRfqItemId }: any) => currRfqItemId === modifiedElement?.id
      );
      const itemNote =
        quoteItems?.find(
          ({ rfqItemId: autoRfqId }: any) => autoRfqId === modifiedElement?.id
        )?.note || '';

      const existingItem = {
        ...findItem,
        note: itemNote
      };

      const currProduct =
        modifiedElement?.matchedInternalProducts?.[
          modifiedElement?.internalIndex
        ];

      if (currProduct) {
        const quoteProduct = await fetchProductPricing(
          currProduct,
          existingItem,
          modifiedElement
        );
        setPendingQuoteProduct(quoteProduct);
      }
    }

    closeDialog('more-matches');
  };

  const handleProductChange = async (
    p: Product,
    i: number,
    isExternal = true
  ) => {
    const { id, modifiedElement, ...rest } = getProductChange(
      products,
      p,
      i,
      isExternal
    );
    setProduct(modifiedElement);

    setModId(id);
    setNewAnalytics({ ...rest, isExternal });

    // Fetch pricing if quote exists and product is in quote
    if (quote?._id && modifiedElement?.id && modifiedElement?.inQuote) {
      const quoteItems = quote.items || [];
      const existingItem = quoteItems?.find(
        ({ currRfqItemId }: any) => currRfqItemId === modifiedElement?.id
      );
      const currProduct =
        modifiedElement?.matchedInternalProducts?.[
          modifiedElement?.internalIndex
        ];

      if (currProduct) {
        const quoteProduct = await fetchProductPricing(
          currProduct,
          existingItem,
          modifiedElement
        );

        setPendingQuoteProduct(quoteProduct);
      }
    }

    closeDialog('more-matches');
  };

  const handleApiProductChange = async (
    newProduct: Product,
    isExternal = true
  ) => {
    const matchedIndex = product.matchedInternalProducts?.findIndex(
      ({ _id }: { _id: string }) => _id === newProduct._id
    );
    let newIndex = matchedIndex;
    const matchedInternalProducts = product?.matchedInternalProducts || [];

    if (matchedIndex === -1) {
      newIndex = product.matchedInternalProducts?.length || 0;
      matchedInternalProducts.push({ ...newProduct, external: isExternal });
    }

    const mfgSkuMatched =
      product?.matchedInternalProducts?.[newIndex]?.percentage === 100;

    const modifiedElement = {
      ...product,
      manualEntry: false,
      internalIndex: newIndex,
      mfgSkuMatched,
      ...(isExternal && {
        matchOverride: RfqMatchOverride.UserSelection
      }),
      external: isExternal,
      matchedInternalProducts,
      percentage: product?.matchedInternalProducts?.[newIndex]?.percentage || 0,
      prevItemPercentage:
        product?.percentage ||
        product?.matchedInternalProducts?.[newIndex]?.percentage
    };

    const totalUpdates: any = {};

    const newBucket = getItemAnalyticsBucket(modifiedElement?.percentage);
    const oldBucket = getItemAnalyticsBucket(
      modifiedElement?.prevItemPercentage
    );

    if (oldBucket !== newBucket) {
      if (oldBucket) totalUpdates[oldBucket] = -1;
      if (newBucket) totalUpdates[newBucket] = 1;
    }

    setProduct(modifiedElement);
    setModId(product.id);
    setNewAnalytics({
      ...totalUpdates,
      mfgSkuMatched,
      mfgSkuMatchChanged: modifiedElement?.prevItemPercentage === 100,
      isExternal
    });

    // Fetch pricing if quote exists and product is in quote
    if (quote?._id && modifiedElement?.id && modifiedElement?.inQuote) {
      const quoteItems = quote.items || [];
      const existingItem = quoteItems?.find(
        ({ currRfqItemId }: any) => currRfqItemId === modifiedElement?.id
      );
      const currProduct =
        modifiedElement?.matchedInternalProducts?.[
          modifiedElement?.internalIndex
        ];

      if (currProduct) {
        const quoteProduct = await fetchProductPricing(
          currProduct,
          existingItem,
          modifiedElement
        );

        setPendingQuoteProduct(quoteProduct);
      }
    }

    closeDialog('more-matches');
  };

  const handleSubmit = async () => {
    updateProcessedProduct(modId, product);

    const saved = await handleItemUpdate(product);

    const targetProduct = productGetter(product);
    const sourceProduct = productGetter(importedProduct);
    // Capture Databricks Analytics
    captureDBAnalytics({
      eventType: DBEventType.QuotesModule,
      data: {
        rfqId,
        quoteId: quote?._id,
        action: 'jumpToProductMatch',
        ...(saved?.error && { error: saved?.error }),
        tab: 'quote',
        targetProduct: {
          ...(targetProduct?._id && {
            productId: targetProduct?._id
          }),
          ...(targetProduct?.sku && {
            productSku: targetProduct?.sku
          })
        },
        sourceProduct: {
          ...(sourceProduct?._id && {
            productId: sourceProduct?._id
          }),
          ...(sourceProduct?.sku && {
            productSku: sourceProduct?.sku
          })
        },
        source: {
          name: 'RFQDetails'
        }
      }
    });

    // Save failed
    if (!saved) return;

    enqueueSnackbar('Item updated successfully.', {
      variant: 'success'
    });

    // Handle quote item replacement if quote exists and pending quote product is available
    if (quote?._id && product?.id && product?.inQuote && pendingQuoteProduct) {
      const updatedItems = updateQuoteItems(
        quote?.items,
        product?.id,
        pendingQuoteProduct
      );

      const updatedQuote = { ...quote, items: updatedItems };
      setQuote(updatedQuote);

      // Find the quote item id for the current product
      const quoteItem = quote?.items?.find(
        (item: any) => item?.rfqItemId === product?.id
      );

      if (quoteItem) {
        await handleItemUpdate({
          ...product,
          sell: pendingQuoteProduct.sell,
          cost: pendingQuoteProduct?.cost
        });
        updateProcessedProduct(modId, {
          ...product,
          sell: pendingQuoteProduct.sell
        });

        // Clean images array to remove excess properties
        const cleanedImages = pendingQuoteProduct?.images?.map((img: any) => ({
          path: img?.path,
          name: img?.name,
          size: img?.size,
          productId: img?.productId,
          _id: img?._id
        }));

        // Clean vendor object to remove excess properties
        const cleanedVendor = pendingQuoteProduct?.vendor
          ? {
              _id: pendingQuoteProduct.vendor._id,
              name: pendingQuoteProduct.vendor.name,
              vendorId: pendingQuoteProduct.vendor.vendorId
            }
          : undefined;

        const body = Object.fromEntries(
          Object.entries({
            id: quoteItem?.id || quoteItem?._id,
            images: cleanedImages,
            children: [],
            name: pendingQuoteProduct?.name,
            type: pendingQuoteProduct?.type,
            sell: pendingQuoteProduct?.sell || 0,
            cost: pendingQuoteProduct?.cost,
            quantity: pendingQuoteProduct?.quantity,
            note: pendingQuoteProduct?.note,
            sku: pendingQuoteProduct?.sku,
            manufacturerSku: pendingQuoteProduct?.manufacturerSku,
            vendor: cleanedVendor,
            rfqItemId: pendingQuoteProduct?.rfqItemId,
            productId: pendingQuoteProduct?._id,
            unitOfMeasure: pendingQuoteProduct?.unitOfMeasure,
            isEditable: pendingQuoteProduct?.isEditable,
            isFreeHand: pendingQuoteProduct?.isFreeHand,
            minimumQuoteQuantity: pendingQuoteProduct?.minimumQuoteQuantity,
            overrideMinimumQuoteQuantity:
              pendingQuoteProduct?.overrideMinimumQuoteQuantity
          }).filter(([, value]) => value !== undefined)
        );

        await updateQuoteItem({
          quoteId: quote?._id,
          body
        });
      }
    }

    if (newAnalytics == null) {
      onClose?.();
      return;
    }

    onClose?.();
  };

  const captureResourceAnalytics = () =>
    captureAnalytics(
      {
        productSKU: productDetails.sku,
        actionType: ProductActionType.AssetView
      },
      AnalyticsModules.AutoRFQCatalog
    );

  const currSelectedProduct = useMemo(
    () => ({
      ...productGetter(product),
      cost: pendingQuoteProduct?.cost || product?.cost
    }),
    [pendingQuoteProduct?.cost, product]
  );

  return (
    <SBase
      width="80vw"
      header={{
        title: `Product Match Details`,
        onClose
      }}>
      {isRfqItemFetching ? (
        <Box
          style={{
            minHeight: 280,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '26px'
          }}>
          <ProductCard
            key={product?.id}
            index={product?.order ? product?.order : 1}
            summary={product?.summary}
            {...product}
            handleManualEntry={handleManualEntry}
            handleProductChange={handleProductChange}
            ignored={product?.ignored}
            onIgnore={() => {}}
            matched={currSelectedProduct}
            totalMatched={internalProducts?.length}
            internalProducts={internalProducts}
            openProductDetails={() => {
              setProductDetails(currSelectedProduct);
              setIsDialogOpen(true);

              captureAnalytics(
                {
                  productSKU:
                    product.matchedInternalProducts[product?.internalIndex || 0]
                      ?.sku,
                  actionType: ProductActionType.DetailsView
                },
                AnalyticsModules.Catalog
              );
            }}
            showMoreProducts={(i: number) =>
              openDialog(
                <BaseDialog
                  title="Select Match"
                  description={`More Matches for ${product?.productName || product?.desc || '-'} ${product?.competitorSku ? `(SKU: ${product?.competitorSku})` : ''}`}
                  onClose={() => closeDialog('more-matches')}
                  newDesign
                  elements={[
                    ...(internalProducts?.length
                      ? [
                          {
                            content: (
                              <Box width="100%">
                                <ProductMatch
                                  fetch
                                  onClose={() => closeDialog('more-matches')}
                                  ids={internalProducts?.map(
                                    ({ _id }: { _id: string }) => _id
                                  )}
                                  products={internalProducts}
                                  selected={currSelectedProduct}
                                  setSelected={p => {
                                    if (rfqItemId) {
                                      handleApiProductChange(p, false);
                                    } else {
                                      handleProductChange(p, i, false);
                                    }

                                    closeDialog('more-matches');
                                  }}
                                />
                              </Box>
                            ),
                            name: `Top AI Picks`,
                            key: Tabs.PRODUCT_MATCH
                          }
                        ]
                      : []),
                    {
                      content: (
                        <Catalog
                          product={matchedProduct || null}
                          onClose={() => closeDialog('more-matches')}
                          handleSubmit={p => {
                            if (rfqItemId) {
                              handleApiProductChange(p);
                            } else {
                              handleProductChange(p, i);
                            }
                            closeDialog('more-matches');
                          }}
                        />
                      ),
                      name: 'Catalog & Ask AI',
                      key: Tabs.CATALOG
                    },
                    {
                      content: (
                        <ManualEntry
                          onClose={() => closeDialog('more-matches')}
                          handleSubmit={props => {
                            if (rfqItemId) {
                              handleManualApiEntry(props);
                            } else {
                              handleManualEntry(props, i);
                            }
                            closeDialog('more-matches');
                          }}
                        />
                      ),
                      name: 'Manual Entry',
                      key: Tabs.MANUAL
                    }
                  ]}
                />,
                {
                  id: 'more-matches'
                }
              )
            }
          />
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8
            }}>
            <SButton
              color="default"
              variant="text"
              onClick={onClose}
              disabled={isLoading}
              style={{
                borderRadius: 36,
                color: '#5D7083'
              }}>
              Cancel
            </SButton>

            <SButton
              type="submit"
              tooltipTitle={
                productGetter(product)?._id === productId
                  ? 'Please select product'
                  : ''
              }
              disabled={
                productGetter(product)?._id === productId ||
                areItemsUpdating ||
                arePricesLoading ||
                isLoading
              }
              loading={areItemsUpdating || isLoading || arePricesLoading}
              onClick={handleSubmit}
              style={{
                borderRadius: 36,
                paddingLeft: 22
              }}>
              Apply Change
            </SButton>
          </Box>
        </Box>
      )}

      <Dialog
        fullWidth
        maxWidth="md"
        aria-labelledby="product-details"
        open={!!productDetails && isDialogOpen}
        style={{ zIndex: '1300' }}
        onKeyDown={event => {
          if (event.key === 'Escape') setIsDialogOpen(false);
        }}
        classes={{ paper: dialogPaper }}>
        <ProductDetails
          hideProductPrice
          selectedProduct={productDetails}
          selectedCustomerId={null}
          defaultOpenedTab={ProductDetailsTabsIds.PRODUCT_INFO}
          userType={userType}
          canSelectProducts={false}
          captureResourceAnalytics={captureResourceAnalytics}
          openProductDetails={setProductDetails}
          onClose={() => setIsDialogOpen(false)}
          handleCloseActionBar={() => setProductDetails(null)}
        />
      </Dialog>
    </SBase>
  );
};

export default ProductMatchDetails;
